import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function AuthModal({ open, onClose }) {
  const { refresh } = useAuth();
  const [mode, setMode] = useState("login");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async () => {
    if (!phone || !password) return toast.error("Phone y password requeridos");
    setLoading(true);
    try {
      const endpoint = mode === "login" ? "/auth/phone/login" : "/auth/phone/register";
      const body = { phone, password };
      if (mode === "register") body.name = name || phone;
      const { data } = await api.post(endpoint, body);
      localStorage.setItem("famewar_token", data.token);
      await refresh();
      toast.success(mode === "login" ? "Bienvenido de vuelta" : "Cuenta creada");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="auth-modal" className="bg-void-modal border-white/10 max-w-md">
        <DialogHeader>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neon-cyan mb-2">
            ACCESS FAMEWAR
          </div>
          <DialogTitle className="font-heading text-2xl font-black tracking-tight">
            Únete a la batalla
          </DialogTitle>
          <DialogDescription>Inicia sesión con Google o con tu celular.</DialogDescription>
        </DialogHeader>

        <Button
          data-testid="google-signin-button"
          onClick={handleGoogle}
          variant="outline"
          className="w-full border-white/15 hover:border-neon-cyan hover:bg-neon-cyan/5 rounded-full py-6"
        >
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Continuar con Google
        </Button>

        <div className="flex items-center gap-3 my-1">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            O CON CELULAR
          </span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="grid grid-cols-2 bg-black border border-white/10">
            <TabsTrigger data-testid="tab-login" value="login">Login</TabsTrigger>
            <TabsTrigger data-testid="tab-register" value="register">Registro</TabsTrigger>
          </TabsList>
          <TabsContent value="login" className="mt-4 space-y-3">
            <PhoneField phone={phone} setPhone={setPhone} />
            <PasswordField password={password} setPassword={setPassword} />
          </TabsContent>
          <TabsContent value="register" className="mt-4 space-y-3">
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                Nombre (opcional)
              </Label>
              <Input
                data-testid="name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Tu nombre"
                className="mt-1.5 bg-black border-white/10"
              />
            </div>
            <PhoneField phone={phone} setPhone={setPhone} />
            <PasswordField password={password} setPassword={setPassword} />
          </TabsContent>
        </Tabs>

        <Button
          data-testid="submit-auth-button"
          onClick={submit}
          disabled={loading}
          className="w-full bg-neon-cyan hover:bg-neon-cyan/90 text-black font-bold rounded-full py-6 mt-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? "Entrar" : "Crear cuenta"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function PhoneField({ phone, setPhone }) {
  return (
    <div>
      <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
        Celular
      </Label>
      <Input
        data-testid="phone-input"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="+52 55 1234 5678"
        className="mt-1.5 bg-black border-white/10"
      />
    </div>
  );
}

function PasswordField({ password, setPassword }) {
  return (
    <div>
      <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
        Contraseña
      </Label>
      <Input
        data-testid="password-input"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="********"
        className="mt-1.5 bg-black border-white/10"
      />
    </div>
  );
}
